import { useMemo } from "react"

import { Alert, Box, Skeleton, Stack, useMediaQuery } from "@mui/material"
import { SkipassSolid } from "@skistarab/icons"
import theme from "@skistarab/mui-theme"
import { parseISO } from "date-fns"
import { useTranslation } from "react-i18next"

import { OrderSummaryCard, PageContainer, StyledFormList } from "@/components"
import { CheckoutSection } from "@/components/CheckoutSection"
import PassHolderForm from "@/features/PassHolderForm"
import { useDateLocale } from "@/hooks"
import { useEditCartItem, useEditPassQuery, useResorts } from "@/queries/hooks"
import type { PassHolderFormState } from "@/types/PassHolder"
import { navigateToCart } from "@/utils/appshellEvents"
import { getIsSummer } from "@/utils/getSeason"

import {
  mapEditPassToFormState,
  mapEditSummaryData,
  mapFormStateToEditItem,
} from "./helpers"
import { useCartItemIdParam } from "./hooks"

const EditPassHolderDetailsPage = () => {
  const { t } = useTranslation()
  const locale = useDateLocale()
  const { cartItemId } = useCartItemIdParam()
  const { data, isLoading, isError } = useEditPassQuery(cartItemId)
  const { data: resorts } = useResorts()
  const {
    editCartItem,
    isLoading: isSaving,
    error: saveError,
  } = useEditCartItem()
  const isTablet = useMediaQuery(theme.breakpoints.down("md"))

  const isSummer = useMemo(
    () =>
      data?.cartItem.arrivalDate
        ? getIsSummer(parseISO(data.cartItem.arrivalDate))
        : false,
    [data]
  )

  const initialFormState = useMemo(
    () => (data ? mapEditPassToFormState(data) : null),
    [data]
  )

  const summaryData = useMemo(
    () => (data ? mapEditSummaryData(data, resorts, locale) : null),
    [data, resorts, locale]
  )

  const handleFormSave = (updatedPassHolder: PassHolderFormState) => {
    if (!data || !cartItemId) return
    const item = mapFormStateToEditItem(updatedPassHolder, data)
    editCartItem(
      { cartItemId, item },
      {
        onSuccess: (response) => {
          if (response.CartUrl) {
            window.location.href = response.CartUrl
          }
        },
      }
    )
  }

  const onSave = () => {
    const form = document.getElementById(
      "editPassHolderForm"
    ) as HTMLFormElement
    form?.requestSubmit()
  }

  if (!cartItemId || isError) {
    return (
      <PageContainer
        headline={t("editPassHolder.title", {
          context: isSummer ? "summer" : undefined,
        })}
        leftIcon={<SkipassSolid />}
      >
        <Alert severity="error">{t("editPassHolder.fetchError")}</Alert>
      </PageContainer>
    )
  }

  return (
    <PageContainer
      headline={t("editPassHolder.title", {
        context: isSummer ? "summer" : undefined,
      })}
      leftIcon={<SkipassSolid />}
    >
      <Stack
        spacing={{ xs: 2, lg: 4, xxl: 8 }}
        direction={{ xs: "column", md: "row" }}
      >
        {isTablet && (
          <Box sx={{ width: "100%" }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={174} width="100%" />
            ) : (
              <OrderSummaryCard
                destinationName={summaryData?.destinationName ?? ""}
                startDateValue={summaryData?.startDateValue ?? ""}
                bookingLengthName={summaryData?.bookingLengthName ?? ""}
              />
            )}
          </Box>
        )}

        <StyledFormList>
          {isLoading || !initialFormState ? (
            <Skeleton variant="rectangular" height={450} width="100%" />
          ) : (
            <PassHolderForm
              passHolder={initialFormState}
              onSave={handleFormSave}
              usedWtpNumbers={[]}
              formId="editPassHolderForm"
              isEditMode={true}
              startDate={parseISO(data!.cartItem.arrivalDate)}
              resortExternalId={data!.product.resortId}
            />
          )}
        </StyledFormList>

        <Stack
          direction="column"
          spacing={2}
          sx={{
            width: { xs: "100%", lg: 360 },
            flexShrink: 0,
          }}
          flex={1}
          alignSelf="flex-start"
        >
          {!isTablet &&
            (isLoading ? (
              <Skeleton variant="rectangular" height={260} width="100%" />
            ) : (
              <OrderSummaryCard
                destinationName={summaryData?.destinationName ?? ""}
                startDateValue={summaryData?.startDateValue ?? ""}
                bookingLengthName={summaryData?.bookingLengthName ?? ""}
              />
            ))}

          {isLoading ? (
            <Skeleton variant="rectangular" height={100} width="100%" />
          ) : (
            <CheckoutSection
              orderTotalPriceText={summaryData?.orderTotalPriceText ?? "-"}
              isLoading={isSaving}
              error={saveError?.message ?? null}
              onCheckout={onSave}
              buttonLabel={t("editPassHolder.save")}
              loadingLabel={t("editPassHolder.saving")}
              secondaryButtonLabel={t("editPassHolder.cancel")}
              onSecondaryClick={navigateToCart}
            />
          )}
        </Stack>
      </Stack>
    </PageContainer>
  )
}

export default EditPassHolderDetailsPage
